import { useCallback, useEffect, useState } from "react";
import {
  DOCUMENT_SIDES,
  ID_DOCUMENT_VERIFICATION_RETRIABLE_HTTP_STATUS_CODES,
  ID_DOCUMENT_VERIFICATION_STATUSES,
} from "../../../config";
import { useActionCallback } from "../../../../../../utils/hooks/useActionCallback";
import { useIdDocumentVerificationVerifyAdvance } from "./useIdDocumentVerificationVerifyAdvance";

export const useDocumentIdPictureCameraFlow = ({
  documentSide,
  dispatch,
  onSubmitForm,
  onboardingDetailsId,
  idDocumentVerification,
  selectedBase64,
  captureRef,
}) => {
  const [requestedUpload, setRequestedUpload] = useState(false);
  const [uploadingDocumentation, setUploadingDocumentation] = useState(false);

  const getHttpStatusFromRejectedAction = useCallback((err) => {
    return err?.requestStatus ?? err?.payload?.status ?? null;
  }, []);

  const onVerifyDone = useCallback(
    (verificationStatus) => {
      // Camera flow only advances after verify on BACK side.
      if (documentSide !== DOCUMENT_SIDES.BACK) return;
      if (verificationStatus === ID_DOCUMENT_VERIFICATION_STATUSES.COMPLETED) {
        onSubmitForm({});
        return;
      }
      if (verificationStatus === ID_DOCUMENT_VERIFICATION_STATUSES.FAILED) {
        onSubmitForm({});
      }
    },
    [documentSide, onSubmitForm]
  );

  const { isVerifying, requestVerify, pendingVerifyAdvanceRef, finishVerify } =
    useIdDocumentVerificationVerifyAdvance({
      dispatch,
      onboardingDetailsId,
      idDocumentVerification,
      onVerifyDone,
    });

  const clearUploadingDocumentation = useCallback(() => {
    setUploadingDocumentation(false);
  }, []);

  useActionCallback(
    "REQUEST_PUT_ID_DOCUMENT_VERIFICATION_FRONT_FULFILLED",
    clearUploadingDocumentation
  );
  useActionCallback(
    "REQUEST_PUT_ID_DOCUMENT_VERIFICATION_FRONT_REJECTED",
    clearUploadingDocumentation
  );
  useActionCallback(
    "REQUEST_PUT_ID_DOCUMENT_VERIFICATION_BACK_FULFILLED",
    clearUploadingDocumentation
  );
  useActionCallback(
    "REQUEST_PUT_ID_DOCUMENT_VERIFICATION_BACK_REJECTED",
    clearUploadingDocumentation
  );

  useEffect(() => {
    if (!isVerifying) return;
    if (documentSide !== DOCUMENT_SIDES.BACK) return;
    if (idDocumentVerification?.loading) return;
    if (!pendingVerifyAdvanceRef.current) return;
    const verifyDone =
      idDocumentVerification?.lastVerifyStatus ===
        ID_DOCUMENT_VERIFICATION_STATUSES.COMPLETED ||
      idDocumentVerification?.lastVerifyStatus ===
        ID_DOCUMENT_VERIFICATION_STATUSES.FAILED ||
      idDocumentVerification?.status ===
        ID_DOCUMENT_VERIFICATION_STATUSES.COMPLETED ||
      idDocumentVerification?.status ===
        ID_DOCUMENT_VERIFICATION_STATUSES.FAILED;
    if (verifyDone) {
      // Preserve previous behavior: if status is already in store (COMPLETED/FAILED)
      // but we didn't receive the action callback yet, advance immediately.
      finishVerify();
    }
  }, [
    isVerifying,
    documentSide,
    idDocumentVerification?.loading,
    idDocumentVerification?.lastVerifyStatus,
    idDocumentVerification?.status,
    finishVerify,
    pendingVerifyAdvanceRef,
  ]);

  useEffect(() => {
    if (!requestedUpload) return;
    if (idDocumentVerification?.loading) return;
    if (idDocumentVerification?.error) return;

    if (documentSide === DOCUMENT_SIDES.FRONT) {
      onSubmitForm({ documentSide });
      setRequestedUpload(false);
      return;
    }

    if (documentSide === DOCUMENT_SIDES.BACK) {
      setRequestedUpload(false);
      requestVerify();
    }
  }, [
    requestedUpload,
    idDocumentVerification?.loading,
    idDocumentVerification?.error,
    documentSide,
    onSubmitForm,
    requestVerify,
  ]);

  const handleRestartProcess = useCallback(() => {
    dispatch({ type: "RESTART_ID_DOCUMENT_VERIFICATION_FROM_VALIDATION" });
    dispatch({
      type: "REQUEST_POST_ID_DOCUMENT_VERIFICATION_START",
      payload: {
        onboarding_details_id: onboardingDetailsId,
        document_type: idDocumentVerification?.documentType,
      },
    });
  }, [dispatch, idDocumentVerification?.documentType, onboardingDetailsId]);

  const handleVerifyRetry = useCallback(() => {
    dispatch({ type: "CLEAR_ID_DOCUMENT_VERIFICATION_ERROR" });
    requestVerify();
  }, [dispatch, requestVerify]);

  const handleContinueWithPhoto = useCallback(() => {
    setRequestedUpload(true);
    setUploadingDocumentation(true);
    dispatch({ type: "CLEAR_ID_DOCUMENT_VERIFICATION_ERROR" });
    dispatch({
      type:
        documentSide === DOCUMENT_SIDES.FRONT
          ? "REQUEST_PUT_ID_DOCUMENT_VERIFICATION_FRONT"
          : "REQUEST_PUT_ID_DOCUMENT_VERIFICATION_BACK",
      payload: {
        onboarding_details_id: onboardingDetailsId,
        photo: selectedBase64,
      },
    });
  }, [dispatch, documentSide, onboardingDetailsId, selectedBase64]);

  const handleUploadRetry = useCallback(() => {
    setRequestedUpload(false);
    dispatch({ type: "CLEAR_ID_DOCUMENT_VERIFICATION_ERROR" });
  }, [dispatch]);

  const handleCameraRetry = useCallback(() => {
    setRequestedUpload(false);
    const err = idDocumentVerification?.error;
    const isVerifyRejected =
      err?.type === "REQUEST_POST_ID_DOCUMENT_VERIFICATION_VERIFY_REJECTED";
    const isRetriableVerifyError =
      ID_DOCUMENT_VERIFICATION_RETRIABLE_HTTP_STATUS_CODES.includes(
        getHttpStatusFromRejectedAction(err)
      );

    if (isVerifyRejected) {
      if (isRetriableVerifyError) {
        handleVerifyRetry();
        return;
      }

      handleRestartProcess();
      return;
    }

    dispatch({ type: "CLEAR_ID_DOCUMENT_VERIFICATION_ERROR" });
    captureRef.current?.retry?.();
  }, [
    captureRef,
    dispatch,
    getHttpStatusFromRejectedAction,
    handleRestartProcess,
    handleVerifyRetry,
    idDocumentVerification?.error,
  ]);

  return {
    isVerifying,
    uploadingDocumentation,
    handleRestartProcess,
    handleContinueWithPhoto,
    handleUploadRetry,
    handleCameraRetry,
  };
};
